import * as yup from 'yup';

function requiredIfMarried<T extends yup.AnySchema>(
  schemaType: T,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(['maritalStatus '], {
    is: (maritalStatus: string) => maritalStatus !== 'single',
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfPR<T extends yup.AnySchema>(
  schemaType: T,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(['citizenship '], {
    is: (citizenship: string) =>
      citizenship === 'australian_pr_visa' ||
      citizenship === 'australian_tr_visa',
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

function requiredIfCondition<T extends yup.AnySchema>(
  schemaType: T,
  fields: string[],
  condition: (...values: any[]) => boolean,
  message: string,
  disableRequired = true,
): T {
  if (disableRequired) return schemaType.notRequired();

  return schemaType.when(fields, {
    is: (...fieldValues: any[]) => condition(...fieldValues),
    then: (schema: T) => schema.required(message),
    otherwise: (schema: T) => schema.notRequired(),
  });
}

const MAX_PROPERTIES = 6;

type PropertyFields = {
  [key: string]: yup.Schema<any>;
};

const propertyFields: PropertyFields = {};

for (let i = 1; i <= MAX_PROPERTIES; i++) {
  propertyFields[`ausPropertyLocation${i}`] = yup.string().when('ausProperty', {
    is: (ausPropertyValue: any) =>
      typeof ausPropertyValue === 'number' && ausPropertyValue >= i,
    then: (schema) => schema.required(`Please enter property location ${i}`),
    otherwise: (schema) => schema.notRequired(),
  });

  propertyFields[`ausPropertyValue${i}`] = yup
    .number()
    .typeError(`Property value ${i} must be a number`)
    .when('ausProperty', {
      is: (ausPropertyValue: any) =>
        typeof ausPropertyValue === 'number' && ausPropertyValue >= i,
      then: (schema) => schema.required(`Please enter property value ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausPropertyOwner${i}`] = yup.string().when('ausProperty', {
    is: (ausPropertyValue: any) =>
      typeof ausPropertyValue === 'number' && ausPropertyValue >= i,
    then: (schema) => schema.required(`Please select owner ${i}`),
    otherwise: (schema) => schema.notRequired(),
  });

  propertyFields[`ausPropertyUse${i}`] = yup.string().when('ausProperty', {
    is: (ausPropertyValue: any) =>
      typeof ausPropertyValue === 'number' && ausPropertyValue >= i,
    then: (schema) => schema.required(`Please select property usage ${i}`),
    otherwise: (schema) => schema.notRequired(),
  });

  propertyFields[`isMortgageOnProperty${i}`] = yup
    .boolean()
    .when('ausProperty', {
      is: (ausPropertyValue: any) =>
        typeof ausPropertyValue === 'number' && ausPropertyValue >= i,
      then: (schema) => schema.required('Please confirm your mortgage'),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausMortgageLoanLimit${i}`] = yup
    .number()
    .typeError(`Loan limit ${i} must be a number`)
    .when(`isMortgageOnProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Please enter loan limit ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausMortgageLender${i}`] = yup
    .string()
    .when(`isMortgageOnProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Please select Lender`),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausMortgageLoanTerm${i}`] = yup
    .number()
    .typeError(`Loan term ${i} must be a number`)
    .when(`isMortgageOnProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Loan term is required`),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausMortgageInterest${i}`] = yup
    .number()
    .max(100, 'Interest rate cannot exceed 100')
    .typeError(`Interest rate ${i} must be a number`)
    .when(`isMortgageOnProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Interest rate is required`),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausPropertyRent${i}`] = yup
    .number()
    .when(`ausPropertyUse${i}`, {
      is: (ausPropertyValue: any, ausPropertyUseValue: any) =>
        typeof ausPropertyValue === 'number' &&
        ausPropertyValue >= i &&
        (ausPropertyUseValue === 'rented' ||
          ausPropertyUseValue === 'short_term_lease'),
      then: (schema) => schema.required(`Please enter rent ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausPropertyRentFrequency${i}`] = yup
    .string()
    .when(['ausProperty', `ausPropertyUse${i}`], {
      is: (ausPropertyValue: any, ausPropertyUseValue: any) =>
        typeof ausPropertyValue === 'number' &&
        ausPropertyValue >= i &&
        (ausPropertyUseValue === 'rented' ||
          ausPropertyUseValue === 'short_term_lease'),
      then: (schema) => schema.required(`Please enter rent frequency ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausPropertyAmountRequired${i}`] = yup
    .number()
    .when(`ausPropertyPurpose${i}`, {
      is: (ausPropertyUseValue: any) =>
        ausPropertyUseValue === 'refinance_and_cash_out' ||
        ausPropertyUseValue === 'cash_out_only',
      then: (schema) => schema.required(`Please enter amount required ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausMortgageInterestType${i}`] = yup
    .string()
    .when(`ausPropertyPurpose${i}`, {
      is: (ausPropertyUseValue: any) =>
        ausPropertyUseValue === 'refinance_and_cash_out' ||
        ausPropertyUseValue === 'cash_out_only',
      then: (schema) => schema.required(`Interest rate type is required`),
      otherwise: (schema) => schema.notRequired(),
    });

  propertyFields[`ausPropertyAmountPurpose${i}`] = yup
    .string()
    .when(`ausPropertyPurpose${i}`, {
      is: (ausPropertyUseValue: any) =>
        ausPropertyUseValue === 'refinance_and_cash_out' ||
        ausPropertyUseValue === 'cash_out_only',
      then: (schema) =>
        schema.required(`Please specify the property purpose for ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });
}

type OverseasPropertyFields = {
  [key: string]: yup.Schema<any>;
};

const overseasPropertyFields: OverseasPropertyFields = {};

for (let i = 1; i <= MAX_PROPERTIES; i++) {
  overseasPropertyFields[`overseasPropertyLocation${i}`] = yup
    .string()
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required(`Please enter property location ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });
  overseasPropertyFields[`overseasPropertyValue${i}`] = yup
    .number()
    .typeError(`Property value ${i} must be a number`)
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required(`Please enter property value ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasPropertyOwner${i}`] = yup
    .string()
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required(`Please select owner ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasPropertyUse${i}`] = yup
    .string()
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required(`Please select property usage ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`isMortgageOnOverseasProperty${i}`] = yup
    .boolean()
    .when('overseasProperty', {
      is: (overseasPropertyValue: any) =>
        typeof overseasPropertyValue === 'number' && overseasPropertyValue >= i,
      then: (schema) => schema.required('Please confirm your mortgage'),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasMortgageLoanLimit${i}`] = yup
    .number()
    .typeError(`Loan limit ${i} must be a number`)
    .when(`isMortgageOnOverseasProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Please enter loan limit ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasMortgageInterest${i}`] = yup
    .number()
    .max(100, 'Interest rate cannot exceed 100')
    .typeError(`Interest rate ${i} must be a number`)
    .when(`isMortgageOnOverseasProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Interest rate is required`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasMortgageLoanTerm${i}`] = yup
    .number()
    .typeError(`Loan term ${i} must be a number`)
    .when(`isMortgageOnOverseasProperty${i}`, {
      is: true,
      then: (schema) => schema.required(`Loan term is required`),
      otherwise: (schema) => schema.notRequired(),
    });

  overseasPropertyFields[`overseasMortgageRepayment${i}`] = yup
    .number()
    .typeError(`Repayment sum ${i} must be a number`)
    .when(`isMortgageOnOverseasProperty${i}`, {
      is: true,
      then: (schema) =>
        schema.required(`Repayment on overseas mortgage is required`),
      otherwise: (schema) => schema.notRequired(),
    });
  overseasPropertyFields[`overseasPropertyRent${i}`] = yup
    .number()
    .typeError(`Repayment sum ${i} must be a number`)
    .when(`overseasPropertyUse${i}`, {
      is: 'investment_property',
      then: (schema) => schema.required(`Please enter property rent ${i}`),
      otherwise: (schema) => schema.notRequired(),
    });
}

export const primaryDetailSchema = yup.object().shape({
  refinancePriority: yup.array(),
  citizenship: yup.string().nullable(),
  visaSubClass: requiredIfPR(yup.string(), 'Visa sub class is required.'),
  visaExpiryDate: requiredIfPR(yup.string(), 'Visa expire date is required.'),

  country: yup.string(),
  currentResidentalStatus: yup.string().nullable(),
  residentalOtherSpecification: yup.string().when(['residentialStatus '], {
    is: (residentialStatus: string) => residentialStatus === 'other',
    then: (schema) => schema.required('Please provide more details'),
    otherwise: (schema) => schema.notRequired(),
  }),
  maritalStatus: yup.string().nullable(),
  numberOfDependents: yup.number(),
  kidsAge: yup.array().of(yup.number().nullable()),
  privateSchool: yup.string(),
  // numberOfDependents: yup.number().when(['dependentChildren'], {
  //   is: (dependentChildren: boolean) => dependentChildren === true,
  //   then: (schema) =>
  //     schema.required('Number of dependent children is required'),
  //   otherwise: (schema) => schema.notRequired(),
  // }),
  rentExpense: requiredIfCondition(
    yup.number(),
    ['currentResidentalStatus'],
    (residentialStatus: string) => residentialStatus === 'renting',
    'Please enter your rent',
  ),
  frequencyOfRent: requiredIfCondition(
    yup.string(),
    ['currentResidentalStatus'],
    (residentialStatus: string) => residentialStatus === 'renting',
    'Please select your frequency of rent',
  ),
  isTheResidentialPlaceMortgaged: requiredIfCondition(
    yup.boolean(),
    ['currentResidentalStatus'],
    (residentialStatus: string) => residentialStatus === 'live_own_place',
    'Please select any option.',
  ),
  propertyIds: yup.array(),
  overseasPropertyIds: yup.array(),
});

export const personalDetailLabelMapper = {
  refinancePriority: 'What are your refinance priorities?',
  citizenship: 'What’s your citizenship?',
  country: 'Which country do you currently reside in?',
  permanentResident: 'Are you a Permanent Resident(PR) of this country?',
  currentResidentalStatus: 'What’s your current residential status?',
  maritalStatus: 'What is your marital status?',
  dependentChildren: 'Do you have any dependent children?',
};
